import { makeAutoObservable, reaction, runInAction, toJS } from 'mobx';
import {
  ProductLineAttr,
  MarketingTag,
  Portfolio,
  RateSegment,
  Carrier,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { RootStore } from 'store/rootStore';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PortfolioRequest } from 'types/productService';
import { Group } from 'types/groupService';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import { ComparePortfolio } from '@insurancemenu/imenu-components-lib/dist/types/ComparisonBlock';
import { Cart } from 'types/cartService';
import { DomainProductLine, InputFilter } from 'types/domainService';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStore';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { QuoteStatus } from 'types/zywaveService';
import { getCatalog, getPortfolios } from 'api/operations/productServiceV2';
import { getProductLineAttributesFromCatalog } from 'global/services-helpers/productService';
import { getQuoteStatus } from 'api/operations/zywaveService';

export interface AllRatesValues {
  [key: string]: number[];
}

interface PushFn {
  (link: string): void;
}

// A type that specifies which page to redirect to when the back button is clicked.
export type BackType = 'popular' | 'help-me-decide';

export class BrowsingStore {
  rootStore: RootStore;
  loading = false;
  /**
   * List of all product line attributes with their titles
   */
  productLineAttrs: ProductLineAttr[] = [];
  /**
   * List of all available marketing tags with their titles
   */
  marketingTags: MarketingTag[] = [];
  /**
   * Data of the Portfolios without any filtering that happened on FE
   */
  allPortfolios: Portfolio[] = [];
  /**
   * Data that is needed for displaying portfolio and products info in the card component after filter reduced their amount
   */
  filteredPortfolios: Portfolio[] = [];
  /**
   * List of all product lines for displaying product lines buttons. Note(!) Only Product lines with the same rate segment
   */
  productLines: DomainProductLine[] = [];
  /**
   * Current Cart with items and browsing criteria
   */
  cart: Cart = INITIAL_CART_OBJECT;
  /**
   * List of all compared portfolios
   */
  comparedPortfolios: ComparePortfolio[] = [];
  /**
   * Current group data
   */
  group: Group = {
    ...INITIAL_GROUP_OBJECT,
  };

  /**
   * Used to store min and max values of each Rate Tier
   */
  rateValues: AllRatesValues = {};
  /**
   * Store current active ProductLineId. Used as a confirmed product line, because we also stored this value in the URL
   */
  currentProductLineId?: ProductLineId;
  /**
   * Proposal sidebar opening status
   */
  isProposalSidebarOpened = true;
  /**
   * Modal that will indicate for the user that we are waiting for rates
   */
  continuousPullingRatesModalOpened = false;
  /**
   * Counter for amount of calls to Zywave, because API is unreliable and our app might stuck on pulling rates
   */
  calculateRatesAttempts = 0;

  // Setter section
  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  setCurrentProductLineId(currentProductLineId: ProductLineId): void {
    this.currentProductLineId = currentProductLineId;
  }

  setProposalSidebarOpeningStatus(status: boolean): void {
    this.isProposalSidebarOpened = status;
  }

  setFilteredPortfolios(filteredPortfolios: Portfolio[]): void {
    this.filteredPortfolios = filteredPortfolios;
  }

  updateCart(cart: Cart): void {
    this.cart = cart;
  }

  setContinuousPullingRatesModal(opened: boolean): void {
    this.continuousPullingRatesModalOpened = opened;
  }

  incrementRatesAttempts(): void {
    this.calculateRatesAttempts += 1;
  }

  setRatesAttempts(value: number): void {
    this.calculateRatesAttempts = value;
  }

  resetRatesAttempts(): void {
    this.calculateRatesAttempts = 0;
  }

  getCalculateRatesAttempts(): number {
    return this.calculateRatesAttempts;
  }

  // Initial method tha collects all required info for the page
  async collectInitialData(groupId: string, cartId: string, productLineId: ProductLineId): Promise<void> {
    this.setLoading(true);
    this.setCurrentProductLineId(productLineId); // set value right after page render
    const rateSegment = PRODUCT_LINES_CONTENT_MAP[productLineId].rateSegment;

    const res = await this.rootStore.cartStore.getCart(cartId);
    await runInAction(async () => {
      if (!res.data) {
        throw new Error(`cart with such id doesn't exist: ${cartId}`);
      }
      this.cart = res.data.getCart;
      const convertedCart = toJS(this.cart);
      const requests = [
        this.getGroupById(groupId),
        this.getProductLines(this.cart, rateSegment),
        this.getCatalog(
          this.cart.lastUpdateBrowsingCriteria.effectiveDate,
          rateSegment,
          productLineId,
          this.cart.lastUpdateBrowsingCriteria.state,
        ),
      ];
      if (convertedCart.lastUpdateBrowsingCriteria[productLineId]) {
        requests.push(
          this.getPortfolios({
            bc: convertedCart.lastUpdateBrowsingCriteria,
            rateSegment,
            sponsorId: this.rootStore.domainStore.domainHeader.id,
            agencyId: '',
            productLineId,
            groupId,
          }),
        );
      }
      await Promise.all(requests);
      // Show a link to the shopping cart in the header and provide the necessary data for redirection
      this.rootStore.domainStore.setDataForCartRedirect(cartId, groupId, true);
      this.rootStore.domainStore.changeCartItemsQuantity(convertedCart.cartItems.length);

      runInAction(() => {
        const convertedAllPortfolios = this.allPortfolios.map((portfolio) => toJS(portfolio)).slice();
        const convertedProductLineAttrs = this.productLineAttrs.map((attr) => toJS(attr)).slice();
        const carriersFilter = this.createCarriersFilter(productLineId, convertedAllPortfolios);
        // Add the new carrier filter attribute for display in the sidebar filter component when multiple carriers are available
        if (carriersFilter) {
          this.productLineAttrs = [...convertedProductLineAttrs, carriersFilter];
        }
        this.setLoading(false);
      });
    });
  }

  async getCatalog(
    effectiveDate: string,
    rateSegment: RateSegment,
    productLineId: ProductLineId,
    state: string,
  ): Promise<void> {
    const res = await getCatalog(this.rootStore.domainStore.domainHeader.id, effectiveDate, rateSegment, state);
    runInAction(() => {
      const catalog = res.data.getCatalog;
      this.marketingTags = catalog.marketingTags;
      this.productLineAttrs = getProductLineAttributesFromCatalog(catalog, productLineId);
    });
  }

  async getPortfolios(request: PortfolioRequest): Promise<void> {
    const res = await getPortfolios(request);
    runInAction(() => {
      this.allPortfolios = res.data.getPortfolios;
      this.filteredPortfolios = res.data.getPortfolios;
      this.rateValues = this.createRates(res.data.getPortfolios);
      if (this.hasMockRates()) {
        this.setContinuousPullingRatesModal(true);
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getAllCarriersFromProducts(portfolios: Portfolio[]): Carrier[] {
    const allCarriers: Carrier[] = [];
    portfolios.forEach(({ products }) => {
      products.forEach(({ carrier }) => {
        if (!allCarriers.find(({ id }) => id === carrier.id)) {
          allCarriers.push(carrier);
        }
      });
    });

    return allCarriers.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  }

  // eslint-disable-next-line class-methods-use-this
  createCarriersFilter(productLineId: ProductLineId, portfolios: Portfolio[]): ProductLineAttr | undefined {
    const carriers = this.getAllCarriersFromProducts(portfolios);

    const carriersFilter = {
      id: 'carrier',
      displayName: 'carrier',
      productLine: productLineId,
      type: 'carrier',
      category: '',
    };

    return carriers.length > 1 ? carriersFilter : undefined;
  }

  async getGroupById(id: string): Promise<void> {
    await this.rootStore.groupStore.getGroupById(id);
    runInAction(() => {
      this.group = this.rootStore.groupStore.group;
    });
  }

  async getProductLines(cart: Cart, rateSegment: RateSegment): Promise<void> {
    const inputFilter: InputFilter = {
      effectiveDate: cart.lastUpdateBrowsingCriteria.effectiveDate,
      zipCode: cart.lastUpdateBrowsingCriteria.zipCode,
      state: cart.lastUpdateBrowsingCriteria.state,
      county: cart.lastUpdateBrowsingCriteria.county,
      agencyId: '',
      sponsorId: this.rootStore.domainStore.domainHeader.id,
    };
    const res = await this.rootStore.domainStore.getDomainProductLine(inputFilter);
    runInAction(() => {
      this.productLines = res.filter((line) => line.rateSegment === rateSegment); // filter out product lines that have different rate segment
    });
  }

  // Browsing card methods

  async onAddToProposal(portfolioId: string, portfolioName: string): Promise<void> {
    this.setLoading(true);
    if (!this.currentProductLineId) {
      throw new Error('Unsupported ProductLineId');
    }
    const { id, lastUpdateBrowsingCriteria } = this.cart;
    const currentPortfolio = this.allPortfolios.find((portfolio) => portfolio.id === portfolioId);
    const convertedPortfolio = toJS(currentPortfolio);
    const bcVersion = convertedPortfolio && Object.values(convertedPortfolio.bcVersions)[0];

    const res = await this.rootStore.cartStore.addPortfolioToCart(
      {
        cartId: id,
        portfolioId,
        portfolioName,
        browsingCriteria: { ...lastUpdateBrowsingCriteria, ...bcVersion },
        productLineId: this.currentProductLineId,
        bcIndex: null,
      },
      this.rootStore.domainStore.domainHeader.id,
    );
    runInAction(() => {
      const currentCart = res.data?.addPortfolioConfigToCart;
      if (currentCart) {
        this.cart = currentCart;
        this.rootStore.domainStore.changeCartItemsQuantity(currentCart.cartItems.length);
      }
      this.setLoading(false);
    });
  }

  // isLast === true -> remove last bc (click on remove button on card )
  // isLast === false -> remove cart item from right sidebar
  async onRemoveFromProposal(portfolioId: string, isLast: boolean): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.cartStore.removePortfolioFromCart(this.cart.id, portfolioId, isLast);
    runInAction(() => {
      const currentCart = res.data?.removePortfolioConfigFromCart;
      if (currentCart) {
        this.cart = currentCart;
        this.rootStore.domainStore.changeCartItemsQuantity(currentCart.cartItems.length);
      }
      this.setLoading(false);
    });
  }

  async checkZywaveQuotingStatus(): Promise<QuoteStatus> {
    if (!this.currentProductLineId) {
      throw new Error('Unsupported ProductLineId');
    }
    return getQuoteStatus(this.group.id, this.currentProductLineId, this.cart.lastUpdateBrowsingCriteria);
  }

  // Functional section

  // eslint-disable-next-line class-methods-use-this
  createRates(portfolios: Portfolio[]): AllRatesValues {
    // get all values for each rate
    const allRatesValues: AllRatesValues = {};

    portfolios.forEach((portfolio: Portfolio) => {
      portfolio.products.forEach((product) => {
        Object.values(product.bcDependentData[0].rates).forEach(({ displayKey, value }) => {
          if (allRatesValues[displayKey]) {
            allRatesValues[displayKey] = [...allRatesValues[displayKey], value];
          } else {
            allRatesValues[displayKey] = [value];
          }
        });
      });
    });

    // get min and max value for each rate
    const convertedRates: AllRatesValues = {};

    Object.entries(allRatesValues).forEach(([key, values]) => {
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);
      convertedRates[key] = [Math.floor(minValue), Math.ceil(maxValue)];
    });
    return convertedRates;
  }

  // eslint-disable-next-line class-methods-use-this
  onViewPlanDetails(portfolioId: string, cartId: string, pushFn: PushFn): void {
    pushFn(
      `/product-compare?type=details&portfolioId=${portfolioId}&cartId=${cartId}&productLineId=${this.currentProductLineId}&groupId=${this.cart.groupId}&cartType=browsing`,
    );
  }

  onCompare(id: string, compareStatus: boolean): void {
    if (compareStatus) {
      const currentPortfolio = this.allPortfolios.find((portfolio) => {
        return portfolio.id === id;
      });

      if (currentPortfolio) {
        this.comparedPortfolios = [
          ...this.comparedPortfolios,
          {
            id: currentPortfolio.id,
            logoUrl: currentPortfolio.products[0].carrier.logo, // Take carrier logo in the first product in portfolio
            portfolioName: currentPortfolio.name,
          },
        ];
        return;
      }
    }
    this.onRemoveFromComparison(id);
  }

  onRemoveFromComparison(id: string): void {
    this.comparedPortfolios = this.comparedPortfolios.filter((portfolio) => id !== portfolio.id);
  }

  clearComparedPortfolios(): void {
    this.comparedPortfolios = [];
  }

  // eslint-disable-next-line class-methods-use-this
  compareSelectedPortfolios(pushFn: PushFn): void {
    const comparedPortfolioIds = this.comparedPortfolios.map(({ id }) => id).join(',');
    pushFn(
      `/product-compare?type=portfolios&portfolioIds=${comparedPortfolioIds}&cartId=${this.cart.id}&productLineId=${this.currentProductLineId}&groupId=${this.cart.groupId}&cartType=browsing`,
    );
  }

  // eslint-disable-next-line class-methods-use-this
  compareAllPortfolios(pushFn: PushFn): void {
    pushFn(
      `/product-compare?type=all-portfolios&cartId=${this.cart.id}&productLineId=${this.currentProductLineId}&groupId=${this.cart.groupId}&cartType=browsing`,
    );
  }

  // eslint-disable-next-line class-methods-use-this
  compareVariants(portfolioId: string, cartId: string, pushFn: PushFn): void {
    pushFn(
      `/product-compare?type=variants&portfolioId=${portfolioId}&cartId=${cartId}&productLineId=${this.currentProductLineId}&groupId=${this.cart.groupId}&cartType=browsing`,
    );
  }

  compareAllPortfoliosInPl(productLineId: ProductLineId, pushFn: PushFn): void {
    pushFn(
      `/product-compare?type=cart&cartId=${this.cart.id}&productLineId=${productLineId}&groupId=${this.cart.groupId}&cartType=browsing`,
    );
  }

  onConfigurePortfolio(portfolioId: string, cartId: string, pushFn: PushFn): void {
    pushFn(
      `/product-compare?type=config-portfolio&portfolioId=${portfolioId}&cartId=${cartId}&productLineId=${this.currentProductLineId}&groupId=${this.cart.groupId}&cartType=browsing`,
    );
  }

  hasMockRates(): boolean {
    return this.allPortfolios.some((portfolio) =>
      portfolio.products.some((product) => product.bcDependentData[0].rates.some((rate) => rate.key === 'MOCK')),
    );
  }

  resetData(resetState: () => void): void {
    this.productLineAttrs.length = 0;
    this.allPortfolios.length = 0;
    this.filteredPortfolios.length = 0;
    this.updateCart(INITIAL_CART_OBJECT);
    resetState();
    this.clearComparedPortfolios();
  }

  get areFiltersApplied(): boolean {
    return this.allPortfolios.length !== this.filteredPortfolios.length;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false }, { autoBind: true });

    reaction(
      () => this.continuousPullingRatesModalOpened,
      (modalOpened) => {
        if (modalOpened) {
          // create request for the Portfolios outside interval to avoid warning that it's not in reactive context
          const productLineId = this.currentProductLineId || 'GroupMedical';
          const request: PortfolioRequest = {
            bc: this.cart.lastUpdateBrowsingCriteria,
            rateSegment: PRODUCT_LINES_CONTENT_MAP[productLineId].rateSegment,
            sponsorId: this.rootStore.domainStore.domainHeader.id,
            agencyId: '',
            productLineId,
            groupId: this.group.id,
          };
          // if rates mocked, that means that it's Zywave plans, so we have to wait
          // until they will calculate rates. Code below make calls to ZywaveService
          // each 5 seconds and stops, only when the response is RATED, or it was more
          // than 10 attempts
          const interval = setInterval(() => {
            this.checkZywaveQuotingStatus()
              .then(async (status) => {
                if (status === QuoteStatus.RATED) {
                  clearInterval(interval);

                  this.setRatesAttempts(-1);
                  await this.getPortfolios(request);
                  this.setContinuousPullingRatesModal(false);
                } else if (this.getCalculateRatesAttempts() < 10) {
                  this.incrementRatesAttempts();
                } else {
                  throw new Error('Too many attempts to rate Quote');
                }
              })
              .catch((error) => {
                this.setContinuousPullingRatesModal(false);
                clearInterval(interval);
                throw new Error(error as string);
              });
          }, 5000);
        }
      },
    );
  }
}
